import { MktuClassWord } from '@/services/Api/generated'
import { MktuClassStore } from '@/store/Mktu/MktuClass'
import { makeAutoObservable, reaction } from 'mobx'

interface MktuClassPopupStoreConstructorParams {
  mktuClass: MktuClassStore
}

export class MktuClassPopupStore {
  mktuClass: MktuClassStore
  selectedWordsNames: string[] = []

  onlyNice = true
  search = ''

  get wordsWithFilters(): MktuClassWord[] {
    let words = this.mktuClass.words

    if (this.onlyNice) {
      words = words.filter((w) => w.isNice)
    }

    // add missing words
    words = [...words, ...this.missingWords]

    if (words.length !== 0) {
      words = words.filter((w) =>
        w.word.toLowerCase().includes(this.search.toLowerCase())
      )
    }

    return words
  }

  get selectedWords(): MktuClassWord[] {
    return this.selectedWordsNames
      .map((name) => {
        return this.mktuClass.words.find((word) => word.word === name)
      })
      .filter((word) => word != null)
  }

  get selectedNiceWords(): MktuClassWord[] {
    return this.mktuClass.niceWords.filter((w) =>
      this.selectedWordsNames.includes(w.word)
    )
  }

  get selectedWordsNotNice(): MktuClassWord[] {
    const notNice = this.mktuClass.words
      .filter((w) => !w.isNice)
      .filter((w) => this.selectedWordsNames.includes(w.word))

    return [...notNice, ...this.missingWords]
  }

  get missingWords(): MktuClassWord[] {
    if (this.mktuClass.loadingWords || this.mktuClass.words.length === 0) {
      return []
    }
    const missingWordsNames = this.selectedWordsNames.filter((wordName) => {
      return this.mktuClass.words.find((w) => wordName === w.word) == null
    })

    const missingWords = missingWordsNames.map((word) => ({
      uuid: 'missingWord' + word,
      word,
      classNumber: this.mktuClass.number,
      isNice: false,
    }))

    return missingWords
  }

  get selectedWordsWithSearch(): MktuClassWord[] {
    const words = [...this.selectedWords, ...this.missingWords]

    if (this.search) {
      return words.filter((w) =>
        w.word.toLowerCase().includes(this.search.toLowerCase())
      )
    }

    return words
  }

  get isSelectedAll() {
    if (this.mktuClass.loadingWords) {
      return false
    }

    // @TODO тут если будут выбранные слова которых нет в базе,
    // то надо их не учитывать
    if (this.onlyNice) {
      return this.mktuClass.niceWords.length === this.selectedNiceWords.length
    }

    return this.mktuClass.words.length === this.selectedWords.length
  }

  get isChanged() {
    const initialSelectedWordNames = this.mktuClass.selectedWordsNames

    if (initialSelectedWordNames.length !== this.selectedWordsNames.length) {
      return true
    }

    return this.selectedWordsNames.some(
      (name) => !initialSelectedWordNames.includes(name)
    )
  }

  constructor({ mktuClass }: MktuClassPopupStoreConstructorParams) {
    this.mktuClass = mktuClass

    this.onlyNice =
      !this.mktuClass.hasNotNiceWords && this.missingWords.length == 0

    this.selectedWordsNames = mktuClass.selectedWordsNames

    makeAutoObservable(this, {}, { autoBind: true })

    reaction(
      () => this.mktuClass.hasNotNiceWords,
      (value) => {
        this.changeOnlyNice(!value && this.missingWords.length == 0)
      }
    )
  }

  changeOnlyNice(value: boolean) {
    this.onlyNice = value
  }

  selectWord(wordName: MktuClassWord['word']) {
    if (this.selectedWordsNames.includes(wordName)) {
      this.setSelectedWordNames(
        this.selectedWordsNames.filter((w) => w !== wordName)
      )
    } else {
      this.setSelectedWordNames([...this.selectedWordsNames, wordName])
    }
  }

  toggleSelectedAll() {
    if (this.onlyNice) {
      const selectedWordsNotNice = this.selectedWordsNotNice.map((w) => w.word)

      if (this.isSelectedAll) {
        this.setSelectedWordNames(selectedWordsNotNice)
      } else {
        const niceWords = this.mktuClass.niceWords.map((w) => w.word)
        this.setSelectedWordNames([...selectedWordsNotNice, ...niceWords])
      }
    } else if (this.isSelectedAll) {
      this.setSelectedWordNames([])
    } else {
      this.setSelectedWordNames(this.mktuClass.words.map((w) => w.word))
    }
  }

  resetAll() {
    this.setSelectedWordNames([])
  }

  setSelectedWordNames(selectedWordNames: string[]) {
    this.selectedWordsNames = selectedWordNames
  }

  setSearch(value: string) {
    this.search = value
  }

  setMktuClass(mktuClass: MktuClassStore) {
    this.mktuClass = mktuClass
  }
}
